'use client'

import { Button } from '@/components/ui/button'
import type { Product } from '@/payload-types'

import { useCart } from '@payloadcms/plugin-ecommerce/client/react'
import clsx from 'clsx'
import { useSearchParams } from 'next/navigation'
import React, { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { trackAddToCart } from '@/utilities/gtag'

type Props = {
  product: Product
}

export function AddToCart({ product }: Props) {
  const t = useTranslations('product')
  const { addItem, isLoading } = useCart()
  const searchParams = useSearchParams()

  const [quantity] = React.useState(1)

  const selectedVariantData = useMemo(() => {
    if (!product.enableVariants || !product.variants) return null

    const data = { options: [] as any[], priceTotal: 0 }
    let hasMissingSelection = false

    product.variants.forEach((variant) => {
      if (typeof variant === 'object' && variant !== null && 'name' in variant) {
        const selectedValue = searchParams.get(variant.name.toLowerCase())
        if (selectedValue && variant.values) {
          const option = variant.values.find((v) => v.value === selectedValue)
          if (option) {
            data.options.push({
              name: variant.name,
              value: option.value,
              price: option.price || 0,
              sku: option.sku || '',
            })
            if (option.price) data.priceTotal += option.price
          } else {
            hasMissingSelection = true
          }
        } else {
          hasMissingSelection = true
        }
      }
    })

    if (hasMissingSelection) return null
    return data
  }, [product, searchParams])

  const addToCart = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault()

      // If variants are enabled but selection is incomplete, show error
      if (product.enableVariants && !selectedVariantData) {
        toast.error(t('selectVariant') || 'Please select all options')
        return
      }

      addItem(
        {
          product: product.id,
          // @ts-ignore
          selectedVariantData: selectedVariantData,
        },
        quantity,
      ).then(() => {
        toast.success(t('addedToCart'))
        window.dispatchEvent(new CustomEvent('open-cart'))

        // GA4: add_to_cart
        const price = selectedVariantData?.priceTotal || product.basePrice || 0
        const variantLabel = selectedVariantData?.options
          ?.map((o: any) => `${o.name}: ${o.value}`)
          .join(', ')
        trackAddToCart(product, price, quantity, variantLabel)
      })
    },
    [addItem, product, selectedVariantData, quantity, t],
  )

  const disabled = useMemo<boolean>(() => {
    return false
  }, [])

  const isInvalid = product.enableVariants && !selectedVariantData

  return (
    <Button
      aria-label={t('addToCart')}
      variant={'neon-white'}
      className={clsx('font-mono uppercase tracking-wider rounded-lg dark:bg-black dark:text-neon-orange dark:border-white/10', {
        'hover:opacity-90': !isInvalid,
        'cursor-not-allowed opacity-50': disabled || isLoading,
        'opacity-50': isInvalid && !disabled && !isLoading, // Visual indicator only
      })}
      disabled={disabled || isLoading}
      onClick={addToCart}
      type="submit"
    >
      {isLoading ? (
        <div className="flex items-center gap-2">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-inherit" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          {t('addToCart')}
        </div>
      ) : (
        t('addToCart')
      )}
    </Button>
  )
}
